import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Trash2, Eye, Star, Building2, User, Filter, X, Sparkles, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import DOMPurify from 'dompurify';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const NPSBadge = ({ score, small = false }) => {
  let bgColor = 'bg-red-100 text-red-700';
  if (score >= 9) bgColor = 'bg-green-100 text-green-700';
  else if (score >= 7) bgColor = 'bg-yellow-100 text-yellow-700';
  
  return (
    <span className={`inline-flex items-center justify-center font-bold ${bgColor} ${small ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'}`}>
      {score}
    </span>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AdminEvaluations() {
  useAuth(); // ensure authenticated
  const [evaluations, setEvaluations] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEval, setSelectedEval] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // AI Report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportContent, setReportContent] = useState('');

  // Filters
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterAgency, setFilterAgency] = useState('');

  useEffect(() => {
    fetchEvaluations();
    fetchAgencies();
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvaluations = async () => {
    try {
      const response = await axios.get(`${API}/evaluations`);
      setEvaluations(response.data);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const hasActiveFilters = filterStartDate || filterEndDate || filterAgent || filterAgency;

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterAgent('');
    setFilterAgency('');
  };

  const filteredEvaluations = useMemo(() => {
    return evaluations.filter((ev) => {
      if (filterStartDate) {
        const evDate = new Date(ev.created_at).toISOString().split('T')[0];
        if (evDate < filterStartDate) return false;
      }
      if (filterEndDate) {
        const evDate = new Date(ev.created_at).toISOString().split('T')[0];
        if (evDate > filterEndDate) return false;
      }
      if (filterAgent && ev.agent_id !== filterAgent) return false;
      if (filterAgency && ev.agency_id !== filterAgency) return false;
      return true;
    });
  }, [evaluations, filterStartDate, filterEndDate, filterAgent, filterAgency]);

  const handleDelete = async () => {
    if (!selectedEval) return;

    try {
      await axios.delete(`${API}/evaluations/${selectedEval.id}`);
      toast.success('Avaliação eliminada com sucesso');
      setDeleteDialogOpen(false);
      setSelectedEval(null);
      fetchEvaluations();
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      toast.error('Erro ao eliminar avaliação');
    }
  };

  const generateReport = async () => {
    if (filteredEvaluations.length === 0) {
      toast.error('Nenhuma avaliação para analisar');
      return;
    }

    const selectedAgency = agencies.find(a => a.id === filterAgency);
    const selectedAgent = agents.find(a => a.id === filterAgent);

    setReportLoading(true);
    setReportContent('');
    setReportOpen(true);

    try {
      const response = await axios.post(`${API}/reports/generate`, {
        evaluations: filteredEvaluations.map(ev => ({
          agency_name: ev.agency_name,
          agent_name: ev.agent_name,
          agency_nps: ev.agency_nps ?? ev.nps ?? 0,
          agent_nps: ev.agent_nps ?? ev.nps ?? 0,
          agency_review: ev.agency_review || '',
          agent_review: ev.agent_review || '',
          created_at: ev.created_at,
        })),
        filters: {
          agency_name: selectedAgency?.name || null,
          agent_name: selectedAgent?.name || null,
          start_date: filterStartDate || null,
          end_date: filterEndDate || null,
        },
      });

      setReportContent(response.data.report);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Erro ao gerar relatório IA');
      setReportOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  // Markdown to styled HTML renderer
  const renderMarkdown = useCallback((text) => {
    if (!text) return '';

    const inline = (s) =>
      s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
       .replace(/\*(.+?)\*/g, '<em>$1</em>');

    const lines = text.split('\n');
    let html = '';
    let inUl = false;
    let inOl = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isBullet = /^[-*] (.+)/.test(line);
      const isNumbered = /^\d+\.\s+(.+)/.test(line);

      if (!isBullet && inUl) { html += '</ul>'; inUl = false; }
      if (!isNumbered && inOl) { html += '</ol>'; inOl = false; }

      // ## Heading 2
      const h2 = line.match(/^## (.+)$/);
      if (h2) {
        html += `<h2 style="font-size:17px;font-weight:700;color:#212121;margin:20px 0 8px;padding-bottom:4px;border-bottom:1px solid #e5e5e5;font-family:Montserrat,sans-serif;">${inline(h2[1])}</h2>`;
        continue;
      }
      // ### Heading 3
      const h3 = line.match(/^### (.+)$/);
      if (h3) {
        html += `<h3 style="font-size:14px;font-weight:600;color:#333;margin:14px 0 6px;">${inline(h3[1])}</h3>`;
        continue;
      }
      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        html += '<hr style="border:none;border-top:1px solid #e5e5e5;margin:16px 0;">';
        continue;
      }
      // Bullet list
      if (isBullet) {
        if (!inUl) { html += '<ul style="margin:6px 0;padding-left:20px;list-style:disc;">'; inUl = true; }
        const m = line.match(/^[-*] (.+)/);
        html += `<li style="color:#444;font-size:13px;margin-bottom:4px;line-height:1.5;">${inline(m[1])}</li>`;
        continue;
      }
      // Numbered list
      if (isNumbered) {
        if (!inOl) { html += '<ol style="margin:6px 0;padding-left:20px;list-style:decimal;">'; inOl = true; }
        const m = line.match(/^\d+\.\s+(.+)/);
        html += `<li style="color:#444;font-size:13px;margin-bottom:4px;line-height:1.5;">${inline(m[1])}</li>`;
        continue;
      }
      // Empty line
      if (line.trim() === '') {
        html += '<div style="height:6px;"></div>';
        continue;
      }
      // Paragraph
      html += `<p style="color:#444;font-size:13px;line-height:1.6;margin:4px 0;">${inline(line)}</p>`;
    }

    if (inUl) html += '</ul>';
    if (inOl) html += '</ol>';
    return html;
  }, []);

  const downloadPDF = useCallback(async () => {
    const { jsPDF } = await import('jspdf');

    const selectedAgency = agencies.find(a => a.id === filterAgency);
    const selectedAgent = agents.find(a => a.id === filterAgent);
    const title = selectedAgent?.name
      ? `Relatório - ${selectedAgent.name}`
      : selectedAgency?.name
        ? `Relatório - ${selectedAgency.name}`
        : 'Relatório Geral';

    // Load logo as PNG data URL (local SVG → canvas → PNG)
    let logoData = null;
    const logoAspect = 4; // SVG viewBox is 1500x375 → 4:1 ratio
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = '/logo.svg';
      });
      const cW = 800;
      const cH = 200;
      const canvas = document.createElement('canvas');
      canvas.width = cW;
      canvas.height = cH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, cW, cH);
      logoData = canvas.toDataURL('image/png');
    } catch (e) {
      console.warn('Could not load logo for PDF:', e);
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 18;
    const contentW = pageW - margin * 2;
    let y = margin;

    const gold = [181, 152, 90];
    const dark = [33, 33, 33];
    const gray = [100, 100, 100];
    const lightGray = [170, 170, 170];

    const logoH = 8;
    const logoW = logoH * logoAspect;

    const addPageHeader = () => {
      if (logoData) {
        pdf.addImage(logoData, 'PNG', pageW - margin - logoW, margin, logoW, logoH);
      }
      pdf.setDrawColor(...gold);
      pdf.setLineWidth(0.3);
      pdf.line(margin, margin + logoH + 4, pageW - margin, margin + logoH + 4);
      y = margin + logoH + 10;
    };

    const checkPage = (needed = 10) => {
      if (y + needed > pageH - margin) {
        pdf.addPage();
        addPageHeader();
      }
    };

    const stripMd = (s) => s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');

    const writeLine = (text, { fontSize = 10, bold = false, color = gray, indent = 0, spacing = 1.4 } = {}) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(text, contentW - indent);
      const lineH = fontSize * 0.353 * spacing;
      checkPage(lines.length * lineH + 2);
      for (const line of lines) {
        pdf.text(line, margin + indent, y);
        y += lineH;
      }
    };

    // --- First page header with logo + title ---
    if (logoData) {
      pdf.addImage(logoData, 'PNG', pageW - margin - logoW, y, logoW, logoH);
    }
    y += logoH + 10;
    writeLine(title, { fontSize: 18, bold: true, color: dark });
    y += 2;
    writeLine(`Gerado em ${new Date().toLocaleDateString('pt-PT')}  •  ${filteredEvaluations.length} avaliações analisadas`, { fontSize: 8, color: lightGray });
    y += 2;
    pdf.setDrawColor(...gold);
    pdf.setLineWidth(0.6);
    pdf.line(margin, y, pageW - margin, y);
    y += 12;

    // --- Parse markdown and write ---
    const rawLines = reportContent.split('\n');
    for (let i = 0; i < rawLines.length; i++) {
      const line = rawLines[i];

      // ## Heading 2
      const h2 = line.match(/^## (.+)$/);
      if (h2) {
        y += 4;
        checkPage(12);
        writeLine(stripMd(h2[1]), { fontSize: 13, bold: true, color: dark });
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.3);
        pdf.line(margin, y, pageW - margin, y);
        y += 4;
        continue;
      }

      // ### Heading 3
      const h3 = line.match(/^### (.+)$/);
      if (h3) {
        y += 3;
        checkPage(10);
        writeLine(stripMd(h3[1]), { fontSize: 11, bold: true, color: [60, 60, 60] });
        y += 1;
        continue;
      }

      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        y += 3;
        checkPage(6);
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.2);
        pdf.line(margin, y, pageW - margin, y);
        y += 3;
        continue;
      }

      // Bullet list item
      const bullet = line.match(/^[-*] (.+)/);
      if (bullet) {
        checkPage(6);
        pdf.setFontSize(10);
        pdf.setTextColor(...gray);
        pdf.text('•', margin + 3, y);
        writeLine(stripMd(bullet[1]), { indent: 8 });
        y += 0.5;
        continue;
      }

      // Numbered list item
      const numbered = line.match(/^(\d+)\.\s+(.+)/);
      if (numbered) {
        checkPage(6);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...gray);
        pdf.text(`${numbered[1]}.`, margin + 2, y);
        writeLine(stripMd(numbered[2]), { indent: 8 });
        y += 0.5;
        continue;
      }

      // Empty line
      if (line.trim() === '') {
        y += 2;
        continue;
      }

      // Bold-only lines (e.g. **Tema: Atendimento**)
      const boldLine = line.match(/^\*\*(.+)\*\*$/);
      if (boldLine) {
        checkPage(6);
        writeLine(boldLine[1], { bold: true, color: [60, 60, 60] });
        continue;
      }

      // Regular paragraph
      checkPage(6);
      writeLine(stripMd(line));
    }

    // --- Footer ---
    y += 8;
    checkPage(10);
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(margin, y, pageW - margin, y);
    y += 4;
    pdf.setFontSize(7);
    pdf.setTextColor(...lightGray);
    pdf.setFont('helvetica', 'normal');
    const footerText = 'Relatório gerado por IA  •  Opinião Contacto Directo';
    const footerW = pdf.getTextWidth(footerText);
    pdf.text(footerText, (pageW - footerW) / 2, y);

    pdf.save(`relatorio-ia-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF descarregado');
  }, [agencies, agents, filterAgency, filterAgent, filteredEvaluations.length, reportContent]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-evaluations">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
          Avaliações
        </h1>
        <p className="text-gray-500 mt-1">Histórico de todas as avaliações recebidas</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#B5985A]" />
          <span className="label-uppercase">Filtros</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-red-500"
            >
              <X className="w-3 h-3" />
              Limpar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Data início</label>
            <Input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="h-9 border-gray-200 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Data fim</label>
            <Input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="h-9 border-gray-200 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Agência</label>
            <select
              value={filterAgency}
              onChange={(e) => setFilterAgency(e.target.value)}
              className="w-full h-9 border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#B5985A]"
            >
              <option value="">Todas</option>
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Consultor</label>
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="w-full h-9 border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#B5985A]"
            >
              <option value="">Todos</option>
              {agents
                .filter((ag) => !filterAgency || ag.agency_id === filterAgency)
                .map((ag) => (
                  <option key={ag.id} value={ag.id}>{ag.name}</option>
                ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          {hasActiveFilters ? (
            <p className="text-xs text-gray-400">
              {filteredEvaluations.length} de {evaluations.length} avaliações
            </p>
          ) : <span />}
          <Button
            onClick={generateReport}
            disabled={filteredEvaluations.length === 0 || reportLoading}
            className="bg-[#B5985A] hover:bg-[#a08347] text-white text-sm h-9 gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Gerar Relatório IA
          </Button>
        </div>
      </div>

      {filteredEvaluations.length === 0 ? (
        <div className="bg-white border border-gray-100 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            {hasActiveFilters ? 'Nenhuma avaliação encontrada' : 'Nenhuma avaliação'}
          </h3>
          <p className="text-gray-400">
            {hasActiveFilters
              ? 'Tente ajustar os filtros'
              : 'As avaliações dos clientes aparecerão aqui'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Agência</th>
                  <th>Consultor</th>
                  <th>NPS Agência</th>
                  <th>NPS Consultor</th>
                  <th>Comentário</th>
                  <th className="text-right">Acções</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvaluations.map((evaluation) => (
                  <tr key={evaluation.id} data-testid={`evaluation-row-${evaluation.id}`}>
                    <td className="text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(evaluation.created_at)}
                    </td>
                    <td className="text-gray-600 text-sm">
                      {evaluation.agency_name || '-'}
                    </td>
                    <td className="text-gray-600 text-sm">
                      {evaluation.agent_name || '-'}
                    </td>
                    <td>
                      <NPSBadge score={evaluation.agency_nps ?? evaluation.nps ?? 0} />
                    </td>
                    <td>
                      <NPSBadge score={evaluation.agent_nps ?? evaluation.nps ?? 0} />
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {evaluation.agent_review_video && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 mr-1" title="Avaliação em vídeo">
                            Vídeo
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          {evaluation.agency_review.length > 40
                            ? evaluation.agency_review.substring(0, 40) + '...'
                            : evaluation.agency_review}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedEval(evaluation);
                            setDetailsOpen(true);
                          }}
                          className="hover:bg-[#B5985A]/10 hover:text-[#B5985A]"
                          data-testid={`view-evaluation-${evaluation.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedEval(evaluation);
                            setDeleteDialogOpen(true);
                          }}
                          className="hover:bg-red-50 hover:text-red-500"
                          data-testid={`delete-evaluation-${evaluation.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="evaluation-details-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>
              Detalhes da Avaliação
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Visualização completa da avaliação submetida
            </p>
          </DialogHeader>
          {selectedEval && (
            <div className="space-y-6">
              {/* Header info */}
              <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 -mx-6 -mt-2">
                <div>
                  <p className="text-sm text-gray-500">Data</p>
                  <p className="font-medium">{formatDate(selectedEval.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Agência</p>
                  <p className="font-medium">{selectedEval.agency_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Consultor</p>
                  <p className="font-medium">{selectedEval.agent_name || '-'}</p>
                </div>
              </div>

              {/* NPS Scores */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-[#B5985A]" />
                    <span className="text-sm font-medium text-gray-600">NPS Agência</span>
                  </div>
                  <span className="text-2xl font-bold">{selectedEval.agency_nps ?? selectedEval.nps ?? 0}/10</span>
                </div>
                <div className="p-4 bg-gray-50 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#B5985A]" />
                    <span className="text-sm font-medium text-gray-600">NPS Consultor</span>
                  </div>
                  <span className="text-2xl font-bold">{selectedEval.agent_nps ?? selectedEval.nps ?? 0}/10</span>
                </div>
              </div>

              {/* Agency Review */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-[#B5985A]" />
                  <span className="label-uppercase">Comentário sobre a Agência</span>
                </div>
                <div className="bg-gray-50 p-4 text-gray-700">
                  {selectedEval.agency_review || 'Sem comentário'}
                </div>
              </div>

              {/* Agent Review */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-[#B5985A]" />
                  <span className="label-uppercase">Comentário sobre o Consultor</span>
                </div>
                <div className="bg-gray-50 p-4 text-gray-700">
                  {selectedEval.agent_review || 'Sem comentário'}
                </div>
              </div>

              {/* Agent Video Review */}
              {selectedEval.agent_review_video && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#B5985A]" />
                    <span className="label-uppercase">Vídeo do Consultor</span>
                  </div>
                  <div className="bg-gray-50 p-4">
                    <video
                      src={selectedEval.agent_review_video}
                      controls
                      playsInline
                      className="w-full max-h-64 object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-evaluation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Avaliação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar esta avaliação?
              Esta acção é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-evaluation"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="ai-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Montserrat' }}>
              <Sparkles className="w-5 h-5 text-[#B5985A]" />
              Relatório IA
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Análise gerada por inteligência artificial com base em {filteredEvaluations.length} avaliações
            </p>
          </DialogHeader>
          {reportLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-8 h-8 text-[#B5985A] animate-spin" />
              <p className="text-gray-500 text-sm">A gerar análise...</p>
            </div>
          ) : (
            <>
              <div
                className="overflow-y-auto flex-1 pr-2 text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(reportContent)) }}
              />
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <Button
                  variant="outline"
                  onClick={() => setReportOpen(false)}
                  className="text-sm"
                >
                  Fechar
                </Button>
                <Button
                  onClick={downloadPDF}
                  className="bg-[#B5985A] hover:bg-[#a08347] text-white text-sm gap-2"
                >
                  <Download className="w-4 h-4" />
                  Descarregar PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
